/**
 * admin_promo_scans_controller.js — Admin > Promo > All Scans charts (Stimulus).
 *
 * Draws the page's five Chart.js charts (daily, weekly, by agent, by country,
 * by store type) and redraws the daily one for the chosen range (30, 90, 180
 * days, or all time, which starts at the first scan). Charts mount through
 * window.TurboChart (turbo-utils.js), which destroys a canvas's previous chart
 * before drawing and tears every chart down before Turbo caches the page.
 *
 * Chart.js is loaded here, from chartSrc, through _load_script.js.
 *
 * Values: series (the server's label and count arrays, keyed as the view
 * names them), chartSrc (the vendored chart.js). Targets: rangeLabel. Range
 * buttons carry data-days params.
 *
 * Template: templates/admin/promo_scans.html
 */

import { Controller } from "@hotwired/stimulus";
import { loadScript } from "./_load_script.js";

const PALETTE = [
    'rgba(13,110,253,0.7)', 'rgba(25,135,84,0.7)', 'rgba(255,193,7,0.7)',
    'rgba(220,53,69,0.7)', 'rgba(13,202,240,0.7)', 'rgba(111,66,193,0.7)',
    'rgba(253,126,20,0.7)', 'rgba(102,16,242,0.7)', 'rgba(32,201,151,0.7)',
    'rgba(214,51,132,0.7)'
];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CHART_IDS = ['dailyChart', 'weeklyChart', 'agentChart', 'countryChart', 'storeTypeChart'];
const STEP_ONE = { beginAtZero: true, ticks: { stepSize: 1 } };

export default class extends Controller {
    static values = { series: Object, chartSrc: String };
    static targets = ['rangeLabel'];

    connect() {
        loadScript(this.chartSrcValue, 'Chart')
            .then(() => { if (this.element.isConnected) this._drawAll(); })
            .catch(function (error) { console.error('Scan charts not drawn:', error); });
    }

    disconnect() {
        CHART_IDS.forEach(function (id) { window.TurboChart.destroy(id); });
    }

    range(event) {
        event.currentTarget.parentElement.querySelectorAll('.btn').forEach(function (button) {
            button.classList.toggle('active', button === event.currentTarget);
        });
        this._drawDaily(event.params.days);
    }

    _drawAll() {
        const s = this.seriesValue;
        this._drawDaily(30);
        window.TurboChart.mount('weeklyChart', {
            type: 'bar',
            data: { labels: s.weekly_labels, datasets: [{ label: 'Scans', data: s.weekly_counts, backgroundColor: 'rgba(25,135,84,0.7)', borderColor: 'rgba(25,135,84,1)', borderWidth: 1 }] },
            options: { responsive: true, plugins: { legend: { display: false } }, scales: { y: STEP_ONE, x: { ticks: { maxTicksLimit: 6 } } } }
        });
        mountBreakdown('agentChart', s.agent_labels, s.agent_counts);
        window.TurboChart.mount('countryChart', {
            type: 'doughnut',
            data: { labels: s.country_labels, datasets: [{ data: s.country_counts, backgroundColor: PALETTE.slice(0, s.country_labels.length), borderWidth: 1 }] },
            options: { responsive: true, plugins: { legend: { display: true, position: 'bottom', labels: { boxWidth: 12 } } } }
        });
        mountBreakdown('storeTypeChart', s.store_type_labels, s.store_type_counts);
    }

    _drawDaily(days) {
        const data = dailyRange(this.seriesValue.daily_labels, this.seriesValue.daily_counts, days);
        const radius = days > 90 ? 0 : 2;
        window.TurboChart.mount('dailyChart', {
            type: 'line',
            data: { labels: data.labels, datasets: [{ label: 'Scans', data: data.counts, borderColor: 'rgba(13,110,253,1)', backgroundColor: 'rgba(13,110,253,0.1)', fill: true, tension: 0.3, pointRadius: radius, pointHoverRadius: 4 }] },
            options: { responsive: true, plugins: { legend: { display: false } }, scales: { y: STEP_ONE, x: { ticks: { maxTicksLimit: days > 90 ? 15 : 10 } } } }
        });
        this.rangeLabelTarget.textContent = days > 0 ? '(Last ' + days + ' Days)' : '(All Time)';
    }
}

/** A horizontal bar chart of one breakdown (agents, store types). */
function mountBreakdown(id, labels, counts) {
    window.TurboChart.mount(id, {
        type: 'bar',
        data: { labels: labels, datasets: [{ label: 'Scans', data: counts, backgroundColor: PALETTE.slice(0, labels.length), borderWidth: 0 }] },
        options: { responsive: true, plugins: { legend: { display: false } }, indexAxis: 'y', scales: { x: STEP_ONE } }
    });
}

/**
 * One label and count per day from the range's first day to today, zero on a
 * day with no scans. days 0 means all time: from the first day that has one.
 */
function dailyRange(dates, counts, days) {
    const byDate = {};
    dates.forEach(function (date, i) { byDate[date] = counts[i]; });
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let start = today;
    if (days > 0) start = addDays(today, -(days - 1));
    else if (dates.length > 0) start = new Date(dates[0]);

    const labels = [];
    const series = [];
    for (let day = new Date(start); day <= today; day = addDays(day, 1)) {
        const key = isoDate(day);
        labels.push(MONTHS[day.getMonth()] + ' ' + day.getDate());
        series.push(byDate[key] || 0);
    }
    return { labels: labels, counts: series };
}

function addDays(date, n) {
    const next = new Date(date);
    next.setDate(next.getDate() + n);
    return next;
}

function isoDate(date) {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return date.getFullYear() + '-' + (month < 10 ? '0' : '') + month + '-' + (day < 10 ? '0' : '') + day;
}
